import { useEffect } from 'react';
import { useParams } from '@tanstack/react-router';

import { usePublicShareMeta } from '../api/public-share-api';
import { ExpiredLinkPage } from '../components/ExpiredLinkPage';
import { NotFoundLinkPage } from '../components/NotFoundLinkPage';
import { PublicChartHeader } from '../components/PublicChartHeader';
import { PublicDocumentsList } from '../components/PublicDocumentsList';
import { PublicEncountersList } from '../components/PublicEncountersList';
import { PublicFlowsheet } from '../components/PublicFlowsheet';
import { PublicPregnanciesList } from '../components/PublicPregnanciesList';
import { PublicUploadedDocumentsList } from '../components/PublicUploadedDocumentsList';
import { getAxiosErrorStatus, getDetailReason } from '../utils/errors';

export function PublicChartPage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const { data, error, isLoading } = usePublicShareMeta(token);

  // Security meta tags: no indexing, no referrer leakage.
  useEffect(() => {
    const m1 = document.createElement('meta');
    m1.name = 'robots';
    m1.content = 'noindex,nofollow,noarchive,nosnippet';
    document.head.appendChild(m1);

    const m2 = document.createElement('meta');
    m2.name = 'referrer';
    m2.content = 'no-referrer';
    document.head.appendChild(m2);

    return () => {
      m1.remove();
      m2.remove();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  const status = getAxiosErrorStatus(error);

  if (status === 404) return <NotFoundLinkPage />;

  if (status === 410) {
    const reason = getDetailReason(error) === 'revoked' ? 'revoked' : 'expired';
    return <ExpiredLinkPage reason={reason} />;
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm">
        Something went wrong.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/20">
      <header className="border-b bg-card">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-2 px-6 py-3 text-xs">
          <div className="flex items-center gap-2 font-medium">
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">ConsultNow</span>
            <span className="text-muted-foreground">Shared patient chart</span>
          </div>
          <div className="text-muted-foreground">
            Read-only · Link expires{' '}
            <span className="font-medium text-foreground">
              {new Date(data.expires_at).toLocaleString()}
            </span>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-4xl space-y-5 p-6">
        {data.scope.demographics && <PublicChartHeader patient={data.patient} />}
        {data.scope.pregnancies && <PublicPregnanciesList token={token} />}
        {data.scope.flowsheet && <PublicFlowsheet token={token} />}
        {data.scope.encounters && <PublicEncountersList token={token} />}
        {data.scope.generated_documents && <PublicDocumentsList token={token} />}
        {data.scope.uploaded_documents && <PublicUploadedDocumentsList token={token} />}
      </main>
    </div>
  );
}
